import {
  InformalFeedbackRequest,
  InformalFeedbackRequestsClient,
  InformalFeedbackRequestState,
} from 'api/clients/InformalFeedbackRequestsClient'
import { Button } from 'blaze-ui/Button'
import { Plus } from 'blaze-ui/icons'
import { Permission, RecipientsSelectorValueType, SettingsMember } from 'types'
import Styles from './AddMoreReviewers.module.scss'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import { useWorkspaceChannels } from 'hooks/useWorkspaceChannels'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRef, useState } from 'react'
import { ChannelValue, EmailValue, UserValue, Value } from 'components/ShareSheet/RecipientSelector/Types'
import { SharedRecipientSelector } from 'components/ShareSheet/RecipientSelector/SharedRecipientSelector'
import cx from 'classnames'
import Foco from 'react-foco'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { getActiveReviews } from 'utils/informalFeedbackRequest'
import { useModals } from 'almanac-ui/Modal'
import { InviteMoreReviewersModal } from './InviteMoreReviewersModal'
import { useRefreshInformalFeedbackRequests } from 'hooks/useRefreshInformalFeedbackRequests'
import { observer } from 'mobx-react-lite'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useRootStore } from 'stores/RootStore'

interface Props {
  request: InformalFeedbackRequest
  addNewReviewers: boolean
  setAddNewReviewers: (value: boolean) => void
}

const filterEligibleMembers = (members: SettingsMember[], request: InformalFeedbackRequest) => {
  return members.filter(({ id }) => {
    const currentReviewersIds = getActiveReviews(request).map(({ reviewer }) => reviewer.id)
    return !currentReviewersIds.includes(id) && request.requester.id !== id
  })
}

export const AddMoreReviewers = observer(({ request, addNewReviewers, setAddNewReviewers }: Props) => {
  const article = useCurrentArticleMeta()
  const { designStore } = useRootStore()
  const selectorRef = useRef<any>(null)
  const [newReviewers, setNewReviewers] = useState<Value[]>([])
  const [isSuggesting, setIsSuggesting] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { openModal } = useModals()

  const { refreshRequests } = useRefreshInformalFeedbackRequests({
    almanacFileId: request.almanac_file.id,
    requestId: request.id,
  })

  const currentWorkspace = useCurrentWorkspace()
  const workspaceMembers = useWorkspaceMembers(currentWorkspace?.key)
  const eligibleReviewers = filterEligibleMembers(workspaceMembers, request)
  const workspaceChannels = useWorkspaceChannels(currentWorkspace?.key)
  const recipientsToInvite = newReviewers.filter(
    ({ type }) => type === RecipientsSelectorValueType.Email,
  ) as EmailValue[]

  const hasEditAccess = designStore?.file?.permissions.can_edit || article?.current_user_access === Permission.Edit
  const canEditRequest = hasEditAccess && request.state === InformalFeedbackRequestState.Open

  const userRecipients = newReviewers.filter(({ type }) => type === RecipientsSelectorValueType.User) as UserValue[]
  const channelRecipients = newReviewers.filter(
    ({ type }) => type === RecipientsSelectorValueType.Channel,
  ) as ChannelValue[]

  const reviewers = userRecipients.map((recipient) => ({ id: recipient.value.id, required: false }))
  const channelReviewers = channelRecipients.map((recipient) => ({ id: recipient.value.id, required: false }))
  const emailReviewers = recipientsToInvite.map((recipient) => ({ email: recipient.value, required: false }))

  const handleMorePeople = () => {
    setAddNewReviewers(true)
  }

  const handleInviteMoreReviewers = async () => {
    if (emailReviewers.length > 0) {
      openModal(InviteMoreReviewersModal, {
        onSubmit,
        newRecipients: recipientsToInvite,
      })

      return
    }

    onSubmit({ addMembers: false })
  }

  const onSubmit = async ({ addMembers }: { addMembers: boolean }) => {
    setIsSubmitting(true)

    const res = await InformalFeedbackRequestsClient.createReviews(request.id, {
      subject: request.title,
      message: request.description || '',
      reviewers,
      emailReviewers,
      channelReviewers,
      permission: Permission.Edit,
      addMembers,
    })

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Invites to review sent successfully')
      setAddNewReviewers(false)
      setNewReviewers([])
      refreshRequests()
    } else {
      showErrorMessageToast('Something went wrong, try again')
    }
    setIsSubmitting(false)
  }

  return (
    <div className={Styles.addReviewersContainer}>
      <Button variant="tertiary" size="xs" frontIcon={Plus} onPress={handleMorePeople}>
        Invite More People
      </Button>
      {addNewReviewers && (
        <Foco onClickOutside={() => !isSuggesting && setAddNewReviewers(false)}>
          <div className={Styles.addReviewers}>
            <SharedRecipientSelector
              ref={selectorRef}
              placeholder="Add people or emails"
              sourceData={{ users: eligibleReviewers, channels: workspaceChannels }}
              recipients={newReviewers}
              className={cx(Styles.recipientSelector, { [Styles.withPeople]: newReviewers.length > 0 })}
              onChange={setNewReviewers}
              disabled={!canEditRequest}
              onFocus={() => setIsSuggesting(true)}
              onBlur={() => setIsSuggesting(false)}
            />

            <div className={Styles.buttonContainer}>
              <Button
                variant="tertiary"
                onPress={() => {
                  setAddNewReviewers(false)
                  setNewReviewers([])
                }}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button isDisabled={isSubmitting || newReviewers.length === 0} onPress={handleInviteMoreReviewers}>
                Add Reviewer
              </Button>
            </div>
          </div>
        </Foco>
      )}
    </div>
  )
})
